import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PrintIcon from "@mui/icons-material/Print";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	CircularProgress,
	Paper,
	Skeleton,
	Typography,
} from "@mui/material";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useState } from "react";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { TagLayoutSvg } from "@/controls/tag-label";
import { CommentCell } from "@/pages/mill-overview/components/heat-results/cells";
import { shownTagNo } from "@/pages/sawlines/utils/entryBarcode";
import { InfoGrid } from "@/pages/sawlines/components/primitives";
import {
	usePrintQueueItem,
	usePrintQueue,
	useReprint,
	useTagLayout,
	useTestPrint,
} from "../hooks/usePrinters";
import type { Printer, PrintQueueItemLog, PrintQueueRow } from "../models";
import {
	dpi,
	formatAgo,
	formatDate,
	formatDateTime,
	formatTime,
	shownStatus,
	statusInfo,
	tagSize,
} from "../statusInfo";
import { PrintToast } from "./PrintToast";
import { PrintStatusLabel, PrinterStatusLabel } from "./StatusLabel";

const PREVIEW_WIDTH = 320;

const titleSx = {
	display: "block",
	mb: 0.75,
	fontSize: "0.62rem",
	letterSpacing: "0.08em",
	textTransform: "uppercase",
	color: "text.secondary",
	fontWeight: 700,
} as const;

const placeholderSx = {
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	minHeight: 270,
} as const;

const Panel = ({
	title,
	children,
}: {
	title: string;
	children: React.ReactNode;
}) => (
	<Paper
		variant="outlined"
		sx={{
			px: 1.5,
			py: 1.25,
			borderRadius: 2,
			backgroundColor: "rgba(0,0,0,0.015)",
		}}
	>
		<Typography variant="caption" sx={titleSx}>
			{title}
		</Typography>
		{children}
	</Paper>
);

const printStatusCell = ({ value }: ICellRendererParams) => (
	<PrintStatusLabel status={value} />
);
const statusColumn = {
	field: "Status",
	headerName: "Status",
	headerClass: "left-aligned-header",
	cellStyle: { textAlign: "left" },
	flex: 1,
	minWidth: 108,
	cellRenderer: printStatusCell,
} as const;
const time = ({ value }: { value: Date | null }) => formatTime(value);
const orDash = ({ value }: { value: unknown }) =>
	value == null ? "—" : String(value);
const printerSaid = ({ value }: { value: string | null }) =>
	value ? statusInfo(value).label : "—";

const queueColumns: ColDef<PrintQueueRow>[] = [
	{ field: "PrintQueueItemId", headerName: "Print #", flex: 0.6, minWidth: 70 },
	{
		field: "TagCode",
		headerName: "Tag",
		flex: 1.4,
		minWidth: 105,
		valueFormatter: ({ value }) => (value ? shownTagNo(value) : "Test"),
	},
	statusColumn,
	// a print is sent and printed seconds after it is queued: one date for all three
	{
		colId: "Date",
		headerName: "Date",
		flex: 1,
		minWidth: 95,
		valueGetter: ({ data }) => data?.QueuedAt,
		valueFormatter: ({ value }) => formatDate(value),
	},
	{
		field: "QueuedAt",
		headerName: "Queued",
		flex: 0.8,
		minWidth: 85,
		valueFormatter: time,
	},
	{
		field: "SentAt",
		headerName: "Sent",
		flex: 0.8,
		minWidth: 85,
		valueFormatter: time,
	},
	{
		field: "PrintedAt",
		headerName: "Printed",
		flex: 0.8,
		minWidth: 85,
		valueFormatter: time,
	},
	{ field: "Attempts", headerName: "Attempts", flex: 0.8, minWidth: 80 },
	{
		field: "PrinterSaid",
		headerName: "Printer",
		flex: 1,
		minWidth: 72,
		valueFormatter: printerSaid,
	},
	{
		field: "Message",
		headerName: "Message",
		minWidth: 84,
		maxWidth: 84,
		sortable: false,
		cellRenderer: ({ data }: ICellRendererParams<PrintQueueRow>) => (
			<CommentCell
				comment={data?.Message ?? null}
				label={
					data?.TagCode
						? shownTagNo(data.TagCode)
						: `Print ${data?.PrintQueueItemId}`
				}
			/>
		),
	},
	{
		field: "CreatedBy",
		headerName: "By",
		flex: 1,
		minWidth: 72,
		valueFormatter: orDash,
	},
];

const historyColumns: ColDef<PrintQueueItemLog>[] = [
	{
		field: "LoggedAt",
		headerName: "Time",
		flex: 0.8,
		minWidth: 85,
		valueFormatter: time,
	},
	statusColumn,
	{
		field: "PrinterStatus",
		headerName: "Printer",
		flex: 1,
		minWidth: 95,
		valueFormatter: printerSaid,
	},
	{
		field: "LabelCount",
		headerName: "Tags printed",
		flex: 0.9,
		minWidth: 105,
		valueFormatter: ({ value }) => value?.toLocaleString() ?? "—",
	},
	{
		field: "Message",
		headerName: "Message",
		flex: 2,
		minWidth: 120,
		tooltipField: "Message",
		valueFormatter: orDash,
	},
];

type Props = { printer: Printer | null; onHide: () => void };

export const PrinterDetailModal = ({ printer, onHide }: Props) => {
	const open = !!printer;
	const id = printer?.PrinterId ?? 0;
	const [error, setError] = useState("");
	const [itemId, setItemId] = useState<number | null>(null);
	const [selected, setSelected] = useState<number | null>(null);
	const onError = (e: Error) => setError(e.message);

	const prints = usePrintQueue(id, open, onError);
	const detail = usePrintQueueItem(selected, onError);
	const log = detail.data?.log ?? [];
	const commands = detail.data?.item?.Commands;
	const layout = useTagLayout(selected, commands, onError);
	const test = useTestPrint(setItemId, onError);
	const reprint = useReprint(setItemId, onError);

	const close = () => {
		setSelected(null);
		onHide();
	};
	const ago = formatAgo(printer?.LastContactAt ?? null);

	return (
		<>
			<MesfModal
				id="modal-printer-detail"
				title="PRINTER DETAILS"
				open={open}
				handleClose={close}
				maxWidth="xl"
			>
				<MesfModal.Content
					dividers
					sx={{
						padding: "15px 24px",
						width: "min(calc(100vw - 64px), 1536px)",
						boxSizing: "border-box",
						overflowY: "auto",
						// same width with or without the scrollbar
						scrollbarGutter: "stable",
					}}
				>
					{printer && (
						<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
							<Box
								sx={{
									display: "flex",
									alignItems: "flex-start",
									justifyContent: "space-between",
								}}
							>
								<Box>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
										<Typography
											variant="h6"
											fontWeight={700}
											sx={{ lineHeight: 1.2 }}
										>
											{printer.PrinterName}
										</Typography>
										<PrinterStatusLabel status={shownStatus(printer)} />
									</Box>
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mt: 0.25 }}
									>
										{`${printer.AssetName}  ·  ${printer.IpAddress}:${printer.Port}`}
									</Typography>
								</Box>
								<Button
									variant="contained"
									color="primary"
									size="small"
									disabled={test.isLoading}
									startIcon={
										test.isLoading ? (
											<CircularProgress size="1rem" />
										) : (
											<PrintIcon />
										)
									}
									onClick={() => test.mutate(id)}
								>
									Print test tag
								</Button>
							</Box>

							<Box
								sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
							>
								<Panel title="Printer">
									<InfoGrid
										columns={1}
										items={[
											{ label: "Model", value: printer.Model },
											{ label: "Serial number", value: printer.SerialNumber },
											{ label: "Firmware", value: printer.Firmware },
											{
												label: "Last contact",
												value: `${formatDateTime(printer.LastContactAt)}${ago ? ` · ${ago}` : ""}`,
											},
										]}
									/>
								</Panel>
								<Panel title="Tag setup, read from the printer">
									<InfoGrid
										columns={1}
										items={[
											{ label: "Tag size", value: tagSize(printer) },
											{ label: "Print quality", value: dpi(printer.Dpmm) },
											{
												label: "Print method",
												value:
													printer.IsThermalTransfer == null
														? null
														: printer.IsThermalTransfer
															? "Thermal transfer (uses ribbon)"
															: "Direct thermal",
											},
											{
												label: "Tags printed",
												value: printer.LabelCount?.toLocaleString() ?? null,
											},
										]}
									/>
								</Panel>
							</Box>

							<Panel title="Print queue">
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: `minmax(0, 1fr) ${PREVIEW_WIDTH}px`,
										gap: 2,
									}}
								>
									<Paper
										variant="outlined"
										sx={{ height: "100%", minHeight: 320 }}
									>
										<AgGridReact<PrintQueueRow>
											loading={prints.isLoading}
											loadingOverlayComponent={CenteredLazyLoading}
											overlayNoRowsTemplate="No prints yet"
											rowData={prints.data ?? []}
											columnDefs={queueColumns}
											rowHeight={34}
											headerHeight={30}
											getRowId={(p) => String(p.data.PrintQueueItemId)}
											onRowClicked={(e) => {
												if (e.data) setSelected(e.data.PrintQueueItemId);
											}}
											rowStyle={{ cursor: "pointer" }}
											suppressCellFocus
											gridOptions={{
												rowSelection: {
													mode: "singleRow",
													checkboxes: false,
													enableClickSelection: true,
												},
											}}
										/>
									</Paper>
									{selected == null ? (
										<Paper variant="outlined" sx={placeholderSx}>
											<Typography color="text.secondary">
												Select a print to see its tag
											</Typography>
										</Paper>
									) : (
										<Box>
											{detail.isLoading || layout.isInitialLoading ? (
												<Skeleton variant="rounded" height={270} />
											) : layout.data ? (
												<Paper variant="outlined" sx={{ overflow: "hidden" }}>
													<TagLayoutSvg layout={layout.data} />
												</Paper>
											) : (
												commands === null && (
													<Paper variant="outlined" sx={placeholderSx}>
														<Typography color="text.secondary">
															No tag content saved.
														</Typography>
													</Paper>
												)
											)}
											<Box
												sx={{
													display: "flex",
													justifyContent: "flex-end",
													mt: 1,
												}}
											>
												<Button
													size="small"
													variant="contained"
													disabled={reprint.isLoading}
													startIcon={
														reprint.isLoading ? (
															<CircularProgress size="1rem" />
														) : (
															<PrintIcon />
														)
													}
													onClick={() => reprint.mutate(selected)}
												>
													Reprint
												</Button>
											</Box>
										</Box>
									)}
								</Box>

								{selected != null && (
									<Accordion
										disableGutters
										elevation={0}
										variant="outlined"
										sx={{ mt: 2, "&::before": { display: "none" } }}
									>
										<AccordionSummary expandIcon={<ExpandMoreIcon />}>
											<Typography variant="body2" fontWeight={600}>
												{`Print #${selected} history`}
											</Typography>
										</AccordionSummary>
										<AccordionDetails
											sx={{
												display: "grid",
												gridTemplateColumns: `minmax(0, 1fr) ${PREVIEW_WIDTH}px`,
												alignItems: "start",
												gap: 2,
											}}
										>
											<Paper variant="outlined">
												<AgGridReact<PrintQueueItemLog>
													loading={detail.isLoading}
													loadingOverlayComponent={CenteredLazyLoading}
													rowData={log}
													columnDefs={historyColumns}
													rowHeight={34}
													headerHeight={30}
													domLayout="autoHeight"
													getRowId={(p) => String(p.data.PrintQueueLogId)}
													suppressCellFocus
												/>
											</Paper>
											<Box sx={{ minWidth: 0 }}>
												<Typography variant="caption" sx={titleSx}>
													Printer commands
												</Typography>
												{commands ? (
													<Box
														component="pre"
														sx={{
															m: 0,
															p: 1,
															maxHeight: 220,
															overflow: "auto",
															fontSize: 11,
															bgcolor: "grey.100",
															borderRadius: 1,
															whiteSpace: "pre-wrap",
															wordBreak: "break-all",
														}}
													>
														{commands}
													</Box>
												) : (
													<Typography variant="body2" color="text.secondary">
														No tag content saved.
													</Typography>
												)}
											</Box>
										</AccordionDetails>
									</Accordion>
								)}
							</Panel>
						</Box>
					)}
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "16px 24px" }}>
					<Button variant="contained" color="inherit" onClick={close}>
						Close
					</Button>
				</MesfModal.Actions>
			</MesfModal>
			{itemId != null && (
				<PrintToast
					printQueueItemId={itemId}
					onClose={() => setItemId(null)}
					onError={onError}
					onItemChange={setItemId}
				/>
			)}
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
